import { gql, gqlFetch } from '@/service/gql'
import { graphql, type ResultOf } from 'gql.tada'
import { imageFieldsFrag } from '@/service/fragments/imageFieldsFrag'
import type { SanitizedGql } from '@/util/gqlUtil'

// language=GraphQL
// GH's FindLandingPage renders these via CategoryGroupingWidget's
// ThumbnailNavigator, which reads Category.images[1] as the tile
// backdrop. Pull the Category's own images (rather than the first
// track's images the earlier iteration used) so tiles match the
// artwork Firefly ships for the category itself.
const FETCH_CATEGORIES_QUERY = gql(
    `
        query browseCategories {
            browseCategories(limit: 20) {
                edges {
                    node {
                        id
                        name
                        url
                        images {
                            ...imageFields
                        }
                    }
                }
            }
        }
    `,
    [imageFieldsFrag]
)

/**
 * Browse categories. Cached — the search idle pane requests these on
 * every mount; the response is stable enough to reuse across the
 * session without refetching.
 */
export function fetchCategories() {
    return gqlFetch(FETCH_CATEGORIES_QUERY, undefined, {
        cache: true,
        cacheId: 'browseCategories',
    })
}

// GH's FindLandingPage fetches `browseRecommendations(strategyIds:
// ['FIND:RANKED'], ...)` and renders each returned Recommendation as
// a CategoryGroupingWidget row — the section header on top and a
// horizontal navigator of Category tiles underneath. Mirror that so
// the search idle pane matches GH's rendering.
const FIND_RECOMMENDATIONS_QUERY = gql(
    `
        query findRecommendations(
            $strategyIds: [String]
            $includes: [String]
            $limit: Float
        ) {
            browseRecommendations(
                strategyIds: $strategyIds
                includes: $includes
                limit: $limit
            ) {
                id
                itemType
                contentType
                name
                recommendedContent {
                    edges {
                        node {
                            __typename
                            ... on Category {
                                id
                                name
                                url
                                metricId
                                images {
                                    ...imageFields
                                }
                            }
                        }
                    }
                }
            }
        }
    `,
    [imageFieldsFrag]
)

export type FindRecommendationsResult = SanitizedGql<ResultOf<typeof FIND_RECOMMENDATIONS_QUERY>>

/**
 * Fetches GH's `FIND:RANKED` recommendation groupings. Each Recommendation
 * is a section (name is the header); its recommendedContent.edges are
 * Category-typed tiles.
 */
export function fetchFindRecommendations() {
    return gqlFetch(
        FIND_RECOMMENDATIONS_QUERY,
        {
            strategyIds: ['FIND:RANKED'],
            includes: ['charts', 'newReleases', 'podcast', 'spatialAudio', 'uhd', 'liveHub'],
            limit: 25,
        },
        { cache: true, cacheId: 'findRecommendations' }
    )
}

const CATEGORY_TRACKS_QUERY = graphql(
    `
        query categoryTracks($id: String!) {
            category(id: $id) {
                id
                name
                tracks {
                    edges {
                        node {
                            id
                            title
                            shortTitle
                            duration
                            album {
                                shortTitle
                            }
                            contributingArtists {
                                edges {
                                    node {
                                        name
                                    }
                                }
                            }
                            images {
                                ...imageFields
                            }
                        }
                    }
                }
            }
        }
    `,
    [imageFieldsFrag]
)

/**
 * Retrieves the tracks for a given category.
 * @param id - E.g. 'nSvnecSI' for indie rock
 */
export function fetchCategoryTracks(id: string) {
    return gqlFetch(CATEGORY_TRACKS_QUERY, { id })
}
